//libraries

import { noop } from "lodash";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
//util
import { updateOrgInvite } from "#webapp/src/mutations";
import { page, PageEvents, track, TrackingEvents } from "#webapp/src/util/analytics.util";
import { logger } from "#webapp/src/util/logger.util";
import { getCalViewStateFromDateString } from "#webapp/src/util/team-health.util";
import { getServerUrl } from "@clockwise/web-commons/src/util/post-message-common.util";
// schema and state
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
import { Person } from "@clockwise/schema";
import { IProps } from "./CalendarProfileTypes";
// components
import { HealthPeriodPicker } from "#webapp/src/components/health-period-picker";
import { ProfileCollaborators } from "#webapp/src/components/profile-collaborators";
import { ProfileTimeVisualizations } from "#webapp/src/components/profile-time-visualizations";
import { Button } from "@clockwise/design-system";
import { Mail } from "@clockwise/design-system/icons";
import { CalloutBox } from "@clockwise/web-commons/src/components/callout-box/CalloutBox.tailwind";

// assets and CSS
import {
  openBillingPage,
  openPricingPage,
} from "@clockwise/web-commons/src/util/upgradeLinks.util";
import withStyles from "@material-ui/core/styles/withStyles";
import classNames from "classnames";
import toast from "react-hot-toast";
import { useMonetization } from "../../hooks/useMonetization";
import { buttonIcon, styles } from "./CalendarProfile.styles";

const CalendarProfileCmpt = ({
  classes,
  calendarId,
  org,
  relayEnvironment,
  onEditRange,
  onChangeSelectedCalendarId,
  initialFetchError,
}: IProps) => {
  const viewWeek = useSelector(
    (state: IReduxState) => state.webExtension.activeViewCalendarProfile.viewWeek,
  );
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const { canTeamHealthAnalyticsBeSeen, shouldUserSeeJoinExistingPlan } = useMonetization();

  useEffect(() => {
    page(PageEvents.TEAM_HEALTH_PROFILE);
  }, []);

  // ~-~-~-~-~-~-~-

  const onInviteSuccess = () => {
    setInviteSending(false);
    setInviteSent(true);
    toast.success("Invite sent!");
  };

  const onInviteFailure = () => {
    setInviteSending(false);
    logger.error("Failed to send invite from Calendar Profile");
    toast.error("Failed to send invite.");
  };

  const sendInvite = (_e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setInviteSending(true);
    updateOrgInvite(
      relayEnvironment,
      { calendarIds: [calendarId], orgRelayId: org.id },
      onInviteSuccess,
      onInviteFailure,
    );

    // callback
    track(TrackingEvents.TEAM_HEALTH.EXPERIMENT_CALENDAR_PROFILE_GATED_TEAM_HEALTH_INVITE_CLICKED, {
      calendarId: calendarId,
    });
  };

  const goToPricing = () => {
    track(TrackingEvents.PAYWALLS.M2_ANALYTICS_TEAM_PRICING_CLICKED);
    openPricingPage();
  };

  const goToCheckout = () => {
    track(TrackingEvents.PAYWALLS.M2_ANALYTICS_TEAM_CHECKOUT_CLICKED);
    window.open(`${getServerUrl()}/app/checkout?teamSelect=true`, "_blank");
  };

  const goToJoinAPlan = () => {
    track(TrackingEvents.PAYWALLS.M2_ANALYTICS_TEAM_JOIN_PLAN_CLICKED);
    openBillingPage();
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  const maybeGateTeamHealth = (person: Person | null) => {
    const notAUser = !!person && !person.isUser;

    const displayName =
      !!person && person.profile && person.profile.givenName && person.profile.familyName
        ? `${person.profile.givenName} ${person.profile.familyName}`
        : calendarId;

    if (!canTeamHealthAnalyticsBeSeen) {
      return (
        <div className={classes.gatedContainer}>
          <div className={classes.gatedMessage}>
            Upgrade to the Teams plan to view teammate's analytics.
          </div>
          <div className="cw-flex cw-py-4 cw-gap-5">
            <Button fullWidth size="small" onClick={goToPricing} variant="outlined">
              Compare all Plans
            </Button>
            {shouldUserSeeJoinExistingPlan ? (
              <Button fullWidth size="small" onClick={goToJoinAPlan} sentiment="positive">
                Join a Plan
              </Button>
            ) : (
              <Button fullWidth size="small" onClick={goToCheckout} sentiment="positive">
                Upgrade to Teams
              </Button>
            )}
          </div>
        </div>
      );
    } else if (notAUser) {
      return (
        <div className={classes.gatedContainer}>
          <div className={classes.gatedMessage}>
            {displayName} is not yet using Clockwise, invite {displayName} to view their analytics
          </div>
          <Button disabled={inviteSending || inviteSent} onClick={sendInvite} fullWidth>
            <Mail {...buttonIcon} />
            <div>
              {inviteSending ? "Sending..." : inviteSent ? "Invite Sent" : "Invite to Clockwise"}
            </div>
          </Button>
        </div>
      );
    }

    return <span />;
  };

  // catch errors in fetches
  if (initialFetchError) {
    return (
      <CalloutBox
        sentiment="warning"
        body="Clockwise does not have enough data for this teammate to show their profile."
      />
    );
  }

  const profileViewWeek = viewWeek ? getCalViewStateFromDateString(viewWeek) : undefined;
  let person = null;
  if (
    org?.personsByCalendarIdsErrorable?.__typename === "PersonList" &&
    org?.personsByCalendarIdsErrorable?.list?.length
  ) {
    person =
      org.personsByCalendarIdsErrorable.list.find((p) => p.primaryEmail === calendarId) || null;
  }

  const gateTeamHealth = (!!person && !person.isUser) || !canTeamHealthAnalyticsBeSeen;

  return (
    <div
      className={classNames(classes.relativeWrapper, { [classes.hideOverflow]: gateTeamHealth })}
    >
      {maybeGateTeamHealth(person)}
      <div className={`${classes.container} ${gateTeamHealth ? classes.blur : ""}`}>
        <div className={classes.pickerContainer}>
          <HealthPeriodPicker
            orgId={org?.id || ""}
            disabled={false}
            changeDateRange={onEditRange}
            onChange={noop}
            overrideCalViewState={profileViewWeek}
          />
        </div>
        <ProfileTimeVisualizations
          calendarTimeStats={org?.calendarTimeStats || null}
          userWorkingHours={org?.userWorkingHours || null}
        />
        <ProfileCollaborators org={org} onChangeSelectedCalendarId={onChangeSelectedCalendarId} />
      </div>
    </div>
  );
};

export const CalendarProfile = withStyles(styles)(CalendarProfileCmpt);
